/**
 * Pure helpers for Import CSV: infer entity type, object kind, and validate mapping.
 * Used by ImportSetting; kept in util for testability without mounting React.
 */

export type ImportEntityType = "deal" | "contact_account";

/** One or more entity types selected for import (multi-select). */
export type EntityTypeOption = "CONTACT" | "COMPANY" | "SALE";
export type SelectedEntityTypes = EntityTypeOption[];

export type ObjectTypeKind = "SALE" | "COMPANY" | "CONTACT" | "CONTACT_COMPANY";

/** Convert API objectTypes (string or array) to SelectedEntityTypes for multi-select UI. */
export function objectTypesToSelectedEntityTypes(objectTypes: unknown): SelectedEntityTypes {
  if (Array.isArray(objectTypes)) {
    const upper = objectTypes.map((x: unknown) => String(x).toUpperCase());
    const out: SelectedEntityTypes = [];
    if (upper.includes("CONTACT")) out.push("CONTACT");
    if (upper.includes("COMPANY")) out.push("COMPANY");
    if (upper.includes("SALE")) out.push("SALE");
    return out.length > 0 ? out : ["CONTACT", "COMPANY"];
  }
  if (typeof objectTypes === "string") {
    const parts = objectTypes.toUpperCase().replace(/\s/g, "").split(",");
    const out: SelectedEntityTypes = [];
    if (parts.includes("CONTACT")) out.push("CONTACT");
    if (parts.includes("COMPANY")) out.push("COMPANY");
    if (parts.includes("SALE")) out.push("SALE");
    return out.length > 0 ? out : ["CONTACT", "COMPANY"];
  }
  return ["CONTACT", "COMPANY"];
}

export function inferImportEntityTypeFromMappings(
  mappingsObj: Record<string, unknown[]>
): ImportEntityType {
  if (!mappingsObj || typeof mappingsObj !== "object") return "contact_account";
  const saleLen = (mappingsObj["SALE"]?.length ?? 0) > 0;
  const contactOrCompanyLen =
    (mappingsObj["CONTACT"]?.length ?? 0) > 0 || (mappingsObj["COMPANY"]?.length ?? 0) > 0;
  return saleLen && !contactOrCompanyLen ? "deal" : "contact_account";
}

export function inferImportEntityTypeFromObjectTypes(objectTypes: unknown): ImportEntityType {
  if (Array.isArray(objectTypes)) {
    const upper = objectTypes.map((x: unknown) => String(x).toUpperCase());
    const hasSale = upper.includes("SALE");
    const hasContact = upper.includes("CONTACT");
    const hasCompany = upper.includes("COMPANY");
    return hasSale && !hasContact && !hasCompany ? "deal" : "contact_account";
  }
  if (typeof objectTypes === "string") {
    const upper = objectTypes.toUpperCase().replace(/\s/g, "");
    return upper === "SALE" ||
      (upper.includes("SALE") && !upper.includes("CONTACT") && !upper.includes("COMPANY"))
      ? "deal"
      : "contact_account";
  }
  return "contact_account";
}

export function getObjectTypeKindFromItem(
  item: {
    objectTypes?: unknown;
    mappings?: Record<string, unknown[]>;
  } | null
): ObjectTypeKind | null {
  if (!item) return null;
  const types = item.objectTypes;
  const mappings = item.mappings;

  if (Array.isArray(types)) {
    const upper = types.map((x: unknown) => String(x).toUpperCase());
    const hasSale = upper.includes("SALE");
    const hasContact = upper.includes("CONTACT");
    const hasCompany = upper.includes("COMPANY");
    if (hasSale && !hasContact && !hasCompany) return "SALE";
    if (hasCompany && !hasContact && !hasSale) return "COMPANY";
    if (hasContact && !hasCompany && !hasSale) return "CONTACT";
    if (hasContact || hasCompany) return "CONTACT_COMPANY";
  } else if (typeof types === "string") {
    const upper = types.toUpperCase().replace(/\s/g, "");
    if (upper === "SALE") return "SALE";
    if (upper === "COMPANY") return "COMPANY";
    if (upper === "CONTACT") return "CONTACT";
    if (upper.includes("CONTACT") && upper.includes("COMPANY")) return "CONTACT_COMPANY";
    if (upper.includes("SALE")) return "SALE";
  } else if (mappings && typeof mappings === "object") {
    const hasSale = (mappings["SALE"]?.length ?? 0) > 0;
    const hasContact = (mappings["CONTACT"]?.length ?? 0) > 0;
    const hasCompany = (mappings["COMPANY"]?.length ?? 0) > 0;
    if (hasSale && !hasContact && !hasCompany) return "SALE";
    if (hasCompany && !hasContact && !hasSale) return "COMPANY";
    if (hasContact && !hasCompany && !hasSale) return "CONTACT";
    if (hasContact || hasCompany) return "CONTACT_COMPANY";
  }
  return null;
}

/** Check if mappings include a mapping whose destination field is sourceFieldId (CRM field). */
export function hasMappingForDestination(
  targetedMappings: Array<{ destination?: { value?: string }; source?: { value?: string } }> = [],
  destinationFieldId: string
): boolean {
  if (!Array.isArray(targetedMappings) || targetedMappings.length < 1) return false;
  for (const mapping of targetedMappings) {
    if (mapping?.destination?.value === destinationFieldId) {
      if (mapping?.source?.value) return true;
    }
  }
  return false;
}

export function validateDealMapping(
  saleMappings: Array<{ destination?: { value?: string }; source?: { value?: string } }>,
  errors: string[],
  translate: (key: string) => string
): boolean {
  if (saleMappings.length === 0) {
    errors.push(translate("Map at least one Deal attribute to a file column."));
    return true;
  }
  if (!hasMappingForDestination(saleMappings, "name")) {
    errors.push(translate("Deal name is required."));
  }
  return false;
}

export function validateContactAccountMapping(
  contactMappings: Array<{ destination?: { value?: string }; source?: { value?: string } }>,
  accountMappings: Array<{ destination?: { value?: string }; source?: { value?: string } }>,
  errors: string[],
  translate: (key: string) => string
): boolean {
  const hasContact = contactMappings.length > 0;
  const hasAccount = accountMappings.length > 0;
  if (!hasContact && !hasAccount) {
    errors.push(translate("Map at least one Contact or Account attribute to a file column."));
    return true;
  }
  if (hasContact) {
    if (!hasMappingForDestination(contactMappings, "name")) {
      errors.push(translate("Contact name is required."));
    }
    if (!hasMappingForDestination(contactMappings, "email")) {
      errors.push(translate("Contact email is required."));
    }
  }
  if (hasAccount && !hasMappingForDestination(accountMappings, "name")) {
    errors.push(translate("Account name is required."));
  }
  return false;
}

export function validateImportMapping(
  mappings: Record<
    string,
    Array<{ destination?: { value?: string }; source?: { value?: string } }>
  >,
  importEntityType: ImportEntityType,
  translate: (key: string) => string
): { errors: string[]; hasError: boolean } {
  const selected: SelectedEntityTypes =
    importEntityType === "deal" ? ["SALE"] : ["CONTACT", "COMPANY"];
  return validateImportMappingWithSelectedTypes(mappings, selected, translate);
}

/**
 * Validate mapping based on selected entity types (multi-select).
 * Each selected type must have valid mappings.
 */
export function validateImportMappingWithSelectedTypes(
  mappings: Record<
    string,
    Array<{ destination?: { value?: string }; source?: { value?: string } }>
  >,
  selectedEntityTypes: SelectedEntityTypes,
  translate: (key: string) => string
): { errors: string[]; hasError: boolean } {
  const errors: string[] = [];
  const contactMappings = Array.isArray(mappings["CONTACT"]) ? mappings["CONTACT"] : [];
  const accountMappings = Array.isArray(mappings["COMPANY"]) ? mappings["COMPANY"] : [];
  const saleMappings = Array.isArray(mappings["SALE"]) ? mappings["SALE"] : [];

  if (selectedEntityTypes.length === 0) {
    errors.push(translate("Select at least one type to import (Contacts, Accounts, or Deals)."));
    return { errors, hasError: true };
  }

  if (selectedEntityTypes.includes("SALE")) {
    const hasCriticalError = validateDealMapping(saleMappings, errors, translate);
    if (hasCriticalError) return { errors, hasError: true };
  }

  const hasContact = selectedEntityTypes.includes("CONTACT");
  const hasCompany = selectedEntityTypes.includes("COMPANY");
  if (hasContact || hasCompany) {
    const contactOnly = hasContact && !hasCompany;
    const companyOnly = hasCompany && !hasContact;
    if (contactOnly) {
      if (contactMappings.length === 0) {
        errors.push(translate("Map at least one Contact attribute to a file column."));
        return { errors, hasError: true };
      }
      if (!hasMappingForDestination(contactMappings, "name")) {
        errors.push(translate("Contact name is required."));
      }
      if (!hasMappingForDestination(contactMappings, "email")) {
        errors.push(translate("Contact email is required."));
      }
    } else if (companyOnly) {
      if (accountMappings.length === 0) {
        errors.push(translate("Map at least one Account attribute to a file column."));
        return { errors, hasError: true };
      }
      if (!hasMappingForDestination(accountMappings, "name")) {
        errors.push(translate("Account name is required."));
      }
    } else {
      const hasCriticalError = validateContactAccountMapping(
        contactMappings,
        accountMappings,
        errors,
        translate
      );
      if (hasCriticalError) return { errors, hasError: true };
    }
  }

  return { errors, hasError: errors.length > 0 };
}
